import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import posthog from 'posthog-js';

const EmailValidation = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const [supportText, setSupportText] = useState('');
  const [redirectCountdown, setRedirectCountdown] = useState(null);

  useEffect(() => {
    let isMounted = true;
    let countdownInterval;

    const startRedirectCountdown = () => {
      let seconds = 15;
      setRedirectCountdown(seconds);
      countdownInterval = setInterval(() => {
        seconds -= 1;
        if (seconds <= 0) {
          clearInterval(countdownInterval);
          navigate('/login', { replace: true });
          return;
        }
        if (isMounted) {
          setRedirectCountdown(seconds);
        }
      }, 1000);
    };

    const updateUserAfterValidation = async () => {
      try {
        const storedUser = localStorage.getItem('user');
        if (!storedUser) return;

        const parsed = JSON.parse(storedUser);
        const userId = parsed?.id || parsed?.ID || parsed?.userId;

        if (!userId) {
          console.warn('[EmailValidation] No user ID found to update');
          const updated = { ...parsed, validEmail: 1 };
          localStorage.setItem('user', JSON.stringify(updated));
          setUser(updated);
          return;
        }

        try {
          const profileResponse = await api.get(`/account/profile/${userId}`);
          const profile = profileResponse.data?.profile || {};

          const updatedUser = {
            ...parsed,
            validEmail: 1,
            userRegistrationStep: profile.userRegistrationStep ?? profile.UserRegistrationStep ?? parsed.userRegistrationStep,
          };

          localStorage.setItem('user', JSON.stringify(updatedUser));
          setUser(updatedUser);

          console.log('[EmailValidation] User data updated after email validation', {
            userId,
            validEmail: updatedUser.validEmail,
            userRegistrationStep: updatedUser.userRegistrationStep,
          });
        } catch (profileError) {
          console.warn('[EmailValidation] Failed to fetch profile, updating with basic info', profileError);
          const updated = { ...parsed, validEmail: 1 };
          localStorage.setItem('user', JSON.stringify(updated));
          setUser(updated);
        }
      } catch (error) {
        console.warn('[EmailValidation] Failed to update user data', error);
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          try {
            const parsed = JSON.parse(storedUser);
            if (parsed?.validEmail !== 1) {
              const updated = { ...parsed, validEmail: 1 };
              localStorage.setItem('user', JSON.stringify(updated));
              setUser(updated);
            }
          } catch (e) {
            console.warn('[EmailValidation] Failed to update user cache', e);
          }
        }
      }
    };

    const validateEmail = async () => {
      if (!token) {
        setStatus('error');
        setMessage('Token inválido ou ausente.');
        setSupportText('Solicite um novo email de verificação ou tente novamente mais tarde.');
        return;
      }

      console.group('[EmailValidation] Email validation');
      console.info('Token received', token);

      try {
        setStatus('loading');
        setMessage('');
        setSupportText('');

        const response = await api.get(`/public/email/validate/${token}`);

        console.info('Status', response?.data?.success ? 'success' : 'unknown');

        if (!isMounted) return;

        setStatus('success');
        setMessage('Seu e-mail foi validado com sucesso!');
        setSupportText('Você será redirecionado para a página de login em instantes.');
        posthog.capture('email_validated');
        await updateUserAfterValidation();
        startRedirectCountdown();
      } catch (error) {
        if (!isMounted) return;

        const apiMessage =
          error.response?.data?.message ||
          'Não foi possível validar o email. O link pode ter expirado ou já ter sido utilizado.';

        console.error('Status', 'error', { message: apiMessage });

        setStatus('error');
        setMessage('Não foi possível validar seu e-mail.');
        setSupportText(apiMessage);
      } finally {
        console.groupEnd();
      }
    };

    validateEmail();

    return () => {
      isMounted = false;
      if (countdownInterval) {
        clearInterval(countdownInterval);
      }
    };
  }, [token, navigate, setUser]);

  const renderIcon = () => {
    if (status === 'loading') {
      return (
        <div className="flex items-center justify-center mb-4">
          <span className="h-12 w-12 animate-spin rounded-full border-4 border-white/40 border-t-white"></span>
        </div>
      );
    }

    if (status === 'success') {
      return <i className="bi-check-circle text-green-500 text-5xl mb-4"></i>;
    }

    return <i className="bi-x-circle text-red-500 text-5xl mb-4"></i>;
  };

  const renderActions = () => {
    if (status === 'loading') {
      return null;
    }

    return (
      <div className="mt-6 flex flex-col gap-3">
        <button
          type="button"
          onClick={() => navigate('/login')}
          className="inline-flex items-center justify-center gap-2 rounded-full bg-tBlue px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-tBlue2"
        >
          Ir para login
          <i className="bi-arrow-right-short text-lg"></i>
        </button>
      </div>
    );
  };

  return (
    <div className="bg-[#012B87] bg-fixed min-h-screen">
      <div className="max-w-[392px] mx-auto h-[90vh] flex items-center justify-center px-4 lg:px-0">
        <div className="w-full bg-gradient-to-br from-[#FBEEE8] to-[#E8F6FB] rounded px-5 py-7 sm:px-8 sm:py-9 md:px-10 md:py-11 text-center shadow-xl shadow-slate-900/20">
          {renderIcon()}
          <h1 className="text-lg md:text-2xl font-semibold mb-3">
            {status === 'success' && 'Obrigado!'}
            {status === 'loading' && 'Validando e-mail'}
            {status === 'error' && 'Ops! Algo deu errado'}
          </h1>
          <p className="text-sm text-slate-600 mb-3">{message || 'Estamos processando sua solicitação.'}</p>
          {supportText && <p className="text-xs text-slate-500">{supportText}</p>}

          {redirectCountdown !== null && status === 'success' && (
            <p className="mt-4 text-xs text-slate-500">
              Redirecionando em <span className="font-semibold text-slate-700">{redirectCountdown}s</span>.
            </p>
          )}

          {renderActions()}
        </div>
      </div>
    </div>
  );
};

export default EmailValidation;

